import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, TableController, type TableHeader } from '@/shared/ui';
import type { RegistrationInvite } from '@psp-aggregator/sdk';

const INVITE_STATUS_I18N = {
  active: 'status.active',
  expired: 'status.expired',
  used: 'status.used',
} as const;

const INVITE_STATUS_BADGE = {
  active: 'success',
  expired: 'secondary',
  used: 'warning',
} as const;

export interface InvitationsTableProps {
  invitations: RegistrationInvite[];
}

export const InvitationsTable: FC<InvitationsTableProps> = ({
  invitations,
}) => {
  const { t } = useTranslation();

  const headers: TableHeader[] = [
    {
      key: true,
      title: t('pages.admin.users.columns.status'),
      value: 'status',
    },
    { title: t('pages.admin.users.columns.created'), value: 'created' },
    { title: t('pages.admin.users.columns.expires'), value: 'expires' },
    { title: t('pages.admin.users.columns.uses'), value: 'uses' },
  ];

  const rows = invitations.map((invite) => ({
    created: new Date(invite.created_at).toLocaleString(),
    expires: new Date(invite.expires_at).toLocaleString(),
    id: invite.id,
    status: (
      <Badge variant={INVITE_STATUS_BADGE[invite.status]}>
        {t(INVITE_STATUS_I18N[invite.status])}
      </Badge>
    ),
    uses: `${invite.used_count}/${invite.max_uses}`,
  }));

  return (
    <TableController
      data={rows}
      emptyState={{
        filtered: false,
      }}
      headers={headers}
      mobileItemConfig={{
        subtitleSlot: { key: 'expires' },
        titleSlot: { key: 'status' },
        valueSlot: { key: 'uses' },
      }}
    />
  );
};

InvitationsTable.displayName = 'InvitationsTable';
